import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  ChevronLeft, 
  ChevronDown, 
  ChevronUp, 
  CheckCircle2
} from 'lucide-react';
import DashboardFooter from '../pages/dashboard/DashboardFooter';
import PatternBackground from './PatternBackground';

const TrackClaimsScreen = () => {
  const navigate = useNavigate();
  const [expandedClaim, setExpandedClaim] = useState('medical');

  const handleBack = () => {
    navigate(-1);
  };

  const claims = [
    {
      id: 'medical',
      type: 'Medical Claim',
      ref: 'CLM-2025-0001142',
      status: 'Review Ongoing',
      isCompleted: false,
      timeline: [
        { status: 'done', title: 'Request Received', time: 'Jan 2, 2026 • 10:30 AM' },
        { status: 'current', title: 'Review Ongoing', time: 'In Progress • Est. 2-3 business days' },
        { status: 'upcoming', title: 'Payment Processing', time: 'Jan 3, 2026 • 10:30 AM' },
        { status: 'upcoming', title: 'Claim Paid', time: 'Pending' },
      ]
    },
    {
      id: 'policy',
      type: 'Policy Update',
      ref: 'CLM-2025-0001142',
      status: 'Completed',
      isCompleted: true,
    },
    {
      id: 'document',
      type: 'Document Request',
      ref: 'DOC-2025-00009038',
      status: 'Completed',
      isCompleted: true,
    }
  ];

  return (
    <div className="flex flex-col min-h-screen bg-[#0f1117]">
      <PatternBackground>
        <div className="w-full px-8 pb-20 relative z-[2] flex-1">

          {/* Top Navigation - Full Width, Left Aligned */}
          <div className="sticky top-0 z-10 px-4 py-3 flex items-center">
            <button onClick={handleBack} className="p-2 hover:bg-white/10 rounded-full transition" aria-label="Go back">
              <ChevronLeft size={24} />
            </button>
            <h1 className="ml-3 text-lg font-semibold">Track Claims</h1>
          </div>

           

          {/* Main Content */}
          <div className="flex-1 max-w-4xl mx-auto w-full px-4 pt-4 pb-6">
            <div className="flex justify-between items-center mb-2">
              <h2 className="text-sm font-bold">Track Claims</h2>
              <button className="text-xs text-green-500 font-bold hover:underline">see all</button>
            </div>
            <p className="text-xs text-slate-500 mb-8">Tap any request to view timeline details</p>

            <div className="space-y-4">
              {claims.map((claim) => (
                <div 
                  key={claim.id} 
                  className="bg-[#2D2B3F] rounded-2xl overflow-hidden transition-all duration-300"
                >
                  {/* CLAIM HEADER (ACCORDION TRIGGER) */}
                  <button 
                    onClick={() => setExpandedClaim(expandedClaim === claim.id ? null : claim.id)}
                    className="w-full p-5 flex justify-between items-center text-left hover:bg-white/5 transition-colors"
                  >
                    <div>
                      <h3 className="text-sm font-bold">{claim.type}</h3>
                      <p className="text-[10px] text-slate-500 uppercase font-mono mt-0.5">Ref: {claim.ref}</p>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className={`text-[10px] font-bold ${claim.isCompleted ? 'text-green-500/60' : 'text-green-500'}`}>
                        {claim.status}
                      </span>
                      {expandedClaim === claim.id ? <ChevronUp size={16} className="text-slate-400" /> : <ChevronDown size={16} className="text-slate-400" />}
                    </div>
                  </button>

                  {/* EXPANDABLE TIMELINE CONTENT */}
                  {expandedClaim === claim.id && claim.timeline && (
                    <div className="px-10 pb-10 pt-2 animate-in fade-in slide-in-from-top-2 duration-300">
                      {claim.timeline.map((step, index) => (
                        <TimelineStep 
                          key={index}
                          status={step.status}
                          title={step.title}
                          time={step.time}
                          isLast={index === claim.timeline.length - 1}
                        />
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
           </div>
        </div>
      </PatternBackground>
      <DashboardFooter />
    </div>
  );
};

// TIMELINE STEP SUB-COMPONENT
const TimelineStep = ({ status, title, time, isLast }) => (
  <div className="relative flex gap-6 pb-10 last:pb-0">
    {/* Connecting Line */}
    {!isLast && (
      <div className="absolute left-[7px] top-4 bottom-0 w-[2px] bg-white/10" />
    )}
    
    {/* Status Indicator */}
    <div className="relative z-10 mt-1">
      {status === 'done' && (
        <CheckCircle2 size={16} className="text-green-500 bg-[#2D2B3F] rounded-full" />
      )}
      {status === 'current' && (
        <div className="w-4 h-4 rounded-full border-2 border-white/20 bg-[#2D2B3F] flex items-center justify-center">
          <div className="w-1.5 h-1.5 rounded-full bg-white/40" />
        </div>
      )}
      {status === 'upcoming' && (
        <div className="w-4 h-4 rounded-full border border-white/10 bg-[#2D2B3F]" />
      )}
    </div>

    {/* Text Content */}
    <div>
      <h5 className={`text-xs font-bold ${status === 'upcoming' ? 'text-slate-500' : 'text-white'}`}>
        {title}
      </h5>
      <p className={`text-[10px] mt-1 ${status === 'current' ? 'text-slate-400' : 'text-slate-500'}`}>
        {time}
      </p>
    </div>
  </div>
);

export default TrackClaimsScreen;